import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, FormControlLabel, Switch, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import ConsignerDetails from './components/ConsignerDetails';
import TransporterDetails from './components/TransporterDetails';
import ExportDetails from './components/ExportDetails';
import InquiryReview from './components/InquiryReview';

import axios from 'axios';

const steps = ['Client Details', 'Product Details', 'Dispatch Details', 'Review & Submit'];

const PurcDispatchMaster = ({ mode, connectionString, onPopupCancel }) => {
    const { user, selectedCompany, loading } = useAuth();
    // Safely extract values with fallbacks
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const AppRegID = selectedCompany?.AppRegID || null;
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const params = useParams();               // single call
    const location = useLocation();           // navigation state

    const getInitialPOIds = () => {
        if (location.state?.selectedPOIds && Array.isArray(location.state.selectedPOIds)) {
            return location.state.selectedPOIds;
        }

        const searchParams = new URLSearchParams(location.search);
        const poParam = searchParams.get('po');
        if (poParam) {
            const ids = poParam.split(',').map(Number).filter(n => n > 0);
            if (ids.length > 0) return ids;
        }

        return [];
    };

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [consignorEnabled, setConsignorEnabled] = useState(true);
    const [transporterEnabled, setTransporterEnabled] = useState(true);
    const [exportEnabled, setExportEnabled] = useState(false);
    const [selectedPOIds, setSelectedPOIds] = useState(getInitialPOIds());
    const [isInvoiceLocked, setIsInvoiceLocked] = useState(false);

    const disablePartyName = Array.isArray(formData?.product?.tableData) && formData.product.tableData.some(row => row?.POID != null || row?.PODtsID != null);

    const isFinalLocked = isInvoiceLocked || disablePartyName;

    // ---------- Resolve clientId & challanId ----------
    const paramKey = Object.keys(params).find(key => key.includes('clientId') || key === 'clientId');
    // possible places the parent may pass clientId: route param, location.state
    // const routeClientId = params.clientId ?? params.clientId?.toString?.() ?? null;
    // route param name used in your router earlier was ":SOAutoID". parent navigates to that value as challan id.
    const routeChallanId = params.SOAutoID ?? params.SOAutoID ?? params.id ?? params.challanId ?? null;
    const routeClientId = paramKey ? params[paramKey] : null;

    // prefer location.state values (navigate(..., { state: {...} })) — fallback to route params
    // const editClientId = location.state?.clientId ?? routeClientId ?? null;
    const editClientId = location.state?.clientId ?? (routeClientId ? Number(routeClientId) : null);
    const editChallanIdRaw = location.state?.challanId ?? routeChallanId ?? null;
    const challanId = editChallanIdRaw != null ? Number(editChallanIdRaw) : null;

    console.log("RAW PARAMS FROM useParams()", params);
    // console.log("DETECTED CLIENTID PARAM KEY:", paramKey);
    // console.log("FINAL editClientId:", editClientId);
    console.log("selectedPOIds:", selectedPOIds);

    // ---------- Helper deepMerge ----------
    const deepMerge = (target, source) => {
        const output = { ...target };
        for (const key in source) {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                output[key] = deepMerge(target[key] || {}, source[key]);
            } else {
                output[key] = source[key];
            }
        }
        return output;
    };

    useEffect(() => {
        if (mode !== 'edit') return;

        const challanDtsIds = Array.isArray(formData?.product?.tableData) ? formData.product.tableData.map(r => r.challanDtsID).filter(Boolean) : [];

        if (challanDtsIds.length === 0) return;

        const checkInvoiceLock = async () => {
            try {
                const res = await axios.post(`${API_URL}/check-purc-challan-invoice-lock`, { challanDtsIds });

                if (res.data?.locked) {
                    setIsInvoiceLocked(true);
                }
            } catch (err) {
                console.error('Purchase invoice lock check failed', err);
            }
        };

        checkInvoiceLock();
    }, [mode, formData?.product?.tableData, API_URL]);

    // ---------- Fetch sales orders when in "ref" mode ----------
    useEffect(() => {
        const fetchSalesOrders = async () => {
            try {
                console.log('PurcDispatchMaster (ref mode) - Received data:', { editClientId, selectedPOIds, mode });
                const response = await axios.post(`${API_URL}/purcDispatch-admin/${editClientId}`, { selectedPOIds, companyId });
                const data = response.data;
                console.log(data);

                setFormData(data || {});
            } catch (error) {
                console.error('Error fetching sales orders:', error);
                setSnackbar({ open: true, message: 'Failed to fetch sales order data.', severity: 'error' });
            }
        };
        console.log(mode, editClientId, selectedPOIds, companyId, userId, API_URL);

        if (mode === 'ref' && editClientId && Array.isArray(selectedPOIds) && selectedPOIds.length > 0) {
            fetchSalesOrders();
        }
    }, [mode, editClientId, selectedPOIds, companyId, userId, API_URL]); // ← .length fixes it

    // ---------- Fetch existing challan when editing ----------
    useEffect(() => {
        const fetchChallan = async () => {
            if (!challanId) {
                console.warn('PurcDispatchMaster: no challanId available for edit fetch.');
                setSnackbar({ open: true, message: 'Challan ID not provided for edit mode.', severity: 'warning' });
                return;
            }

            try {
                // If clientId available, send it — otherwise send only challanIds (API should handle)
                const payload = editClientId ? { clientId: editClientId, challanIds: [challanId] } : { challanIds: [challanId] };
                const res = await axios.post(`${API_URL}/Purc-challans`, payload);

                // normalize response — API might return array or single object
                let data = res.data;
                if (Array.isArray(data)) data = data[0] ?? {};
                if (!data || Object.keys(data).length === 0) {
                    throw new Error('Empty challan response');
                }

                console.log('Fetched challan (edit):', data);
                setFormData(data);

                // set toggles based on presence of sections in returned payload
                // setConsignorEnabled(Boolean(data.consigner));
                setTransporterEnabled(Boolean(data.transporter) || Boolean(data.consigner));
                setExportEnabled(Boolean(data.export));

                // setSnackbar({ open: true, message: 'Challan data fetched successfully!', severity: 'success' });
            } catch (err) {
                console.error('Failed to load challan', err);
                if (err.response && err.response.status === 404) {
                    setSnackbar({ open: true, message: 'Challan not found for the given ID and client.', severity: 'error' });
                } else {
                    setSnackbar({ open: true, message: 'Failed to load Challan', severity: 'error' });
                }
            }
        };

        if (mode === 'edit' && challanId) {
            fetchChallan();
        }
    }, [mode, challanId, editClientId]);

    const handleNext = (stepData = {}) => {
        console.log('Step data:', stepData);
        setFormData(prev => deepMerge(prev, stepData));
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleSubmit = async () => {
        try {
            const payload = { client: formData.client, product: formData.product, consigner: transporterEnabled ? formData.consigner : null, transporter: transporterEnabled ? formData.transporter : null, export: exportEnabled ? formData.export : null, companyId, userId, clientId: editClientId || formData.client?.clientId || null, selectedPOIds, mode, AppRegID };

            console.log(mode, 'Submitting payload:', payload);
            if (mode === 'add' || mode === 'ref') {
                await axios.post(`${API_URL}/Purchase-Dispatch`, payload);
            }
            if (mode === 'edit') {
                if (!challanId) throw new Error('Missing challanId for update');
                await axios.put(`${API_URL}/update-PurcDuspatch/${Number(challanId)}`, payload);
            }

            setSnackbar({ open: true, message: mode === 'add' ? 'Challan created successfully!' : 'Challan updated successfully!', severity: 'success' });

            setActiveStep(0);
            setFormData({});
            setSelectedPOIds([]);
            setTimeout(() => {
                if (typeof onPopupCancel === "function") onPopupCancel();
                else navigate(-1);
            }, 600)
        } catch (error) {
            console.error('Submission error:', error);
            setSnackbar({ open: true, message: 'Failed to submit challan.', severity: 'error' });
        }
    };

    const handleCancel = () => {
        if (typeof onPopupCancel === "function") {
            onPopupCancel();
        } else {
            navigate(-1);
        }
    };

    // Sync activeStep with URL changes (e.g., browser back button).
    // Note: by the time popstate fires, the browser has ALREADY moved history
    // back one entry — calling handleCancel()/navigate(-1) here would pop a
    // SECOND entry on top of that, overshooting past the intended page. Just
    // reset local wizard state; the browser's own navigation handles the page.
    useEffect(() => {
        const handlePopState = () => {
            setActiveStep(0);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ──────────────── Alt+C (Cancel) shortcut — works from any step ───────────
    // Attached here (the wizard's parent), not per-step, so it works regardless
    // of which step (Client Details / Product Details / Dispatch Details /
    // Review) is active — those step components don't each carry their own
    // cancel concept.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyC") return;
            e.preventDefault();
            handleCancel();
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [handleCancel]);

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {renderStepHeader('1️⃣', 'Client Details')}
                        <ClientDetails mode={mode} initialData={{ ...formData.client, clientId: editClientId || formData.client?.clientId, challanId }} onNext={(data) => handleNext({ client: data })} connectionString={connectionString} disablePartyName={isFinalLocked} /> </>);

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails initialData={formData.product || {}} L_No={formData.L_No || null} onNext={(data) => handleNext({ product: data })} clientData={formData.client || {}} connectionString={connectionString} mode={mode} isFinalLocked={isFinalLocked}
                            onRowsChange={(rows) => {
                                setFormData(prev => ({
                                    ...prev,
                                    product: {
                                        ...(prev.product || {}),
                                        tableData: rows,
                                    }
                                }));
                            }} />
                    </>
                );

            /* COMBINED STEP (Step 2 consignee and despatch details) */
            case 2:
                return (
                    <>
                        {renderStepHeader('3️⃣', 'Dispatch Details', (<FormControlLabel control={<Switch checked={transporterEnabled} onChange={e => setTransporterEnabled(e.target.checked)} />} label="Enable" />))}
                        {transporterEnabled ? (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: "column", gap: 4 }}>
                                    <ConsignerDetails clientData={formData.client || {}} initialData={formData.consigner} connectionString={connectionString} onChange={(data) => setFormData(prev => ({ ...prev, consigner: data }))} />
                                    <TransporterDetails initialData={formData.transporter || {}} connectionString={connectionString} onChange={(data) => setFormData(prev => ({ ...prev, transporter: data }))} />
                                </Box>


                                {/* ✅ SINGLE NEXT BUTTON */}
                                <Box mt={3} display="flex" justifyContent="flex-end">
                                    <Button variant="contained"
                                        onClick={() => { handleNext({ consigner: formData.consigner, transporter: formData.transporter }); }}
                                    >
                                        Next
                                    </Button>
                                </Box>

                            </>
                        ) : (
                            <Box mt={2}>
                                <Typography color="text.secondary">Dispatch details are skipped.</Typography>
                                <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
                            </Box>
                        )}

                    </>
                );

            case 3:
                return (<InquiryReview formData={{ ...formData, selectedPOIds }} selectedCompany={selectedCompany} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} mode={mode} isInvoiceLocked={isInvoiceLocked} connectionString={connectionString} />);

            default: return null;
        }
    };

    return (
        <Box maxWidth="lg" mx="auto" px={2} py={2} pb={0}>
            {/* <Typography variant="h5" fontWeight="bold" gutterBottom> 📝 Purchase Challan Master </Typography> */}
            {/* <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600, mt: -0.5, mb: 1 }}>
                {mode === 'edit' ? 'Edit Purchase Challan' : 'Add New Purchase Challan'}
            </Typography> */}

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                {/* Back arrow — mobile/tablet only */}
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === 'edit' ? 'Edit Purchase Challan' : 'Add New Purchase Challan'}
                </Typography>
            </Box>
            <Paper elevation={4} sx={{ borderRadius: 3, p: 1.5 }}>
                {isInvoiceLocked && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Challan is locked. Product and Client details cannot be modified.
                    </Alert>
                )}
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Divider sx={{ mb: 0.5 }} />
                {getStepComponent(activeStep)}
            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PurcDispatchMaster;